import { IconPlus } from '@tabler/icons-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { CanWrite } from '../components/CanWrite'
import { Dialog } from '../components/Dialog'
import { SecretDialog } from '../components/SecretDialog'
import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  FieldErrorText,
  LoadingState,
} from '../components/States'
import { hasFieldError, describeError } from '../lib/describeError'
import type { ClientView } from '../lib/apiTypes'
import { useClients, useCreateClient, useMerchants } from '../lib/hooks'
import { rowStatusLabel, rowStatusTone } from '../lib/status'

/** Daftar client beserta tombol buat. Client menyimpan slug, webhook, dan API key. */
export function ClientsPage() {
  const clients = useClients()
  const [creating, setCreating] = useState(false)
  const [createdSecret, setCreatedSecret] = useState<{ secret: string; slug: string } | null>(null)

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Client</h1>
          <p className="muted">
            Client adalah sistem pemanggil Client API. Setiap client punya slug, daftar
            merchant yang diizinkan, URL webhook, dan API key yang bisa dicabut tanpa
            mengganggu client lain.
          </p>
        </div>
        <CanWrite>
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <IconPlus size={18} stroke={1.75} aria-hidden="true" />
            Buat client
          </button>
        </CanWrite>
      </div>

      {clients.isPending ? (
        <LoadingState what="daftar client" />
      ) : clients.isError ? (
        <ErrorState
          title="Gagal memuat daftar client"
          error={clients.error}
          onRetry={() => void clients.refetch()}
          retrying={clients.isFetching}
        />
      ) : (clients.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          title="Belum ada client"
          body="Client dibuat setelah sistem pemanggil terdaftar. Setelah dibuat, API key tampil satu kali dan harus disimpan oleh pemilik sistem."
          action={
            <CanWrite>
              <button type="button" className="btn" onClick={() => setCreating(true)}>
                Buat client pertama
              </button>
            </CanWrite>
          }
        />
      ) : (
        <ClientTable rows={clients.data?.data ?? []} />
      )}

      {creating ? (
        <CreateClientDialog
          onClose={() => setCreating(false)}
          onCreated={(client, secret) => {
            setCreating(false)
            if (secret) {
              setCreatedSecret({ secret, slug: client.slug })
            }
          }}
        />
      ) : null}

      {createdSecret ? (
        <SecretDialog
          title="Webhook secret client baru"
          label="Webhook secret"
          secret={createdSecret.secret}
          note={`Simpan secret ini di sistem client ${createdSecret.slug} untuk memverifikasi tanda tangan webhook.`}
          onClose={() => setCreatedSecret(null)}
        />
      ) : null}
    </>
  )
}

function ClientTable({ rows }: { rows: ClientView[] }) {
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label="Tabel client, geser horizontal dengan tombol panah">
      <table className="table">
        <caption className="visually-hidden">
          Daftar client dengan status, webhook, batas laju, dan merchant yang diizinkan
        </caption>
        <thead>
          <tr>
            <th scope="col">Client</th>
            <th scope="col">Status</th>
            <th scope="col">Webhook</th>
            <th scope="col" className="is-num">
              Batas laju
            </th>
            <th scope="col">Merchant</th>
            <th scope="col">Tindakan</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <span className="fact__value">{row.name}</span>
                <br />
                <span className="caption mono">{row.slug}</span>
              </td>
              <td>
                <Badge tone={rowStatusTone(row.status)}>{rowStatusLabel(row.status)}</Badge>
              </td>
              <td>
                {row.webhook_url ? (
                  <>
                    <span className="mono">{row.webhook_url}</span>
                    <br />
                    <span className="caption">
                      {row.webhook_secret_set ? 'secret terpasang' : 'secret belum ada'}
                      {row.webhook_secret_rotating ? ', sedang rotasi' : ''}
                    </span>
                  </>
                ) : (
                  <span className="muted">tidak memakai webhook</span>
                )}
              </td>
              <td className="is-num">{row.rate_limit_per_minute} per menit</td>
              <td>
                {row.merchants.length > 0 ? (
                  <span className="mono">{row.merchants.join(', ')}</span>
                ) : (
                  <span className="muted">belum ada</span>
                )}
                {row.default_merchant ? (
                  <>
                    <br />
                    <span className="caption">bawaan {row.default_merchant}</span>
                  </>
                ) : null}
              </td>
              <td className="is-actions">
                <Link className="table__link" to={`/clients/${row.id}`}>
                  Buka detail
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CreateClientDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (client: ClientView, secret: string | undefined) => void
}) {
  const create = useCreateClient()
  const merchants = useMerchants()
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [webhookUrl, setWebhookUrl] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [defaultMerchant, setDefaultMerchant] = useState('')

  function toggle(slugValue: string) {
    setSelected((current) =>
      current.includes(slugValue)
        ? current.filter((item) => item !== slugValue)
        : [...current, slugValue],
    )
  }

  function submit() {
    create.mutate(
      {
        slug: slug.trim(),
        name: name.trim(),
        webhook_url: webhookUrl.trim(),
        merchants: selected,
        default_merchant: defaultMerchant,
      },
      {
        onSuccess: (result) => onCreated(result.client, result.webhook_secret),
      },
    )
  }

  return (
    <Dialog
      title="Buat client"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={create.isPending || slug.trim() === '' || name.trim() === ''}
          >
            {create.isPending ? 'Menyimpan...' : 'Simpan client'}
          </button>
        </>
      }
    >
      {create.error ? <Alert tone="danger">{describeError(create.error)}</Alert> : null}

      <div className="form-grid">
        <div className="field">
          <label className="field__label" htmlFor="c-slug">
            Slug
          </label>
          <input
            id="c-slug"
            className="input mono"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            aria-invalid={hasFieldError(create.error, 'slug')}
          />
          <span className="field__hint">
            Huruf kecil, angka, dan tanda hubung. Dipakai di URL dan log.
          </span>
          <FieldErrorText error={create.error} field="slug" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="c-name">
            Nama
          </label>
          <input
            id="c-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-invalid={hasFieldError(create.error, 'name')}
          />
          <FieldErrorText error={create.error} field="name" />
        </div>

        <div className="field form-grid--full">
          <label className="field__label" htmlFor="c-webhook">
            URL webhook
          </label>
          <input
            id="c-webhook"
            className="input"
            type="url"
            placeholder="https://sistem.example.id/webhook/tahupay"
            value={webhookUrl}
            onChange={(event) => setWebhookUrl(event.target.value)}
            aria-invalid={hasFieldError(create.error, 'webhook_url')}
          />
          <span className="field__hint">
            Kosongkan bila client belum memakai webhook. Mengisi URL ini membuat webhook
            secret baru yang tampil satu kali.
          </span>
          <FieldErrorText error={create.error} field="webhook_url" />
        </div>
      </div>

      <div className="field">
        <span className="field__label">Merchant yang diizinkan</span>
        {merchants.isPending ? <LoadingState what="pilihan merchant client" /> : merchants.isError ? <ErrorState title="Gagal memuat pilihan merchant" error={merchants.error} onRetry={() => void merchants.refetch()} /> : (merchants.data?.data ?? []).length === 0 ? (
          <p className="muted">
            Belum ada merchant. Buat merchant lebih dahulu di halaman Merchant, lalu
            kembalikan client ini.
          </p>
        ) : (
          <div className="check-list">
            {(merchants.data?.data ?? []).map((merchant) => (
              <label className="checkbox" key={merchant.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(merchant.slug)}
                  onChange={() => toggle(merchant.slug)}
                />
                {merchant.name} ({merchant.slug})
              </label>
            ))}
          </div>
        )}
        <FieldErrorText error={create.error} field="merchants" />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="c-default">
          Merchant bawaan
        </label>
        <select
          id="c-default"
          className="select"
          value={defaultMerchant}
          onChange={(event) => setDefaultMerchant(event.target.value)}
        >
          <option value="">Tidak ada</option>
          {selected.map((slugValue) => (
            <option key={slugValue} value={slugValue}>
              {slugValue}
            </option>
          ))}
        </select>
        <span className="field__hint">
          Dipakai bila permintaan client tidak menyebut merchant. Harus salah satu dari
          merchant yang dicentang.
        </span>
        <FieldErrorText error={create.error} field="default_merchant" />
      </div>
    </Dialog>
  )
}
